import { i18n } from "@lingui/core";
import { type AriaNumberFieldProps, useNumberField } from "@react-aria/numberfield";
import { useNumberFieldState } from "@react-stately/numberfield";
import {
  assertNever,
  clsx,
  formatNumber,
  i18nParseNumber,
  mergeRefs,
  noop,
  type WithClassName,
} from "@regrello/core-utils";
import React, { useCallback, useRef, useState } from "react";
import { useDeepCompareEffect, useMount } from "react-use";
import { i18nIsNumericPrefixString } from "../../../../core-utils/src/utils/numberFormatUtils";
import { RegrelloButton } from "../../action/button/RegrelloButton";
import { RegrelloButtonGroup } from "../../action/button/RegrelloButtonGroup";
import type { RegrelloIntent } from "../../tokens/enums/regrelloIntent";
import { RegrelloSize } from "../../tokens/enums/regrelloSize";
import { getAccessibleInputLabelId } from "../../utils/a11y/getAccessibleInputLabelId";
import { INPUT_V2_DEFAULT_SIZE } from "../input/inputV2Utils";
import { RegrelloInput, type RegrelloInputProps } from "../input/RegrelloInput";

export interface RegrelloNumericInputProps
  extends Pick<RegrelloInputProps, "id" | "name" | "onKeyDownCapture" | "startElement">,
    Omit<
      AriaNumberFieldProps,
      | "defaultValue"
      | "description"
      | "errorMessage"
      | "isDisabled"
      | "isInvalid"
      | "isReadOnly"
      | "isRequired"
      | "label"
      | "onChange"
      | "validationState"
      | "value"
    >,
    WithClassName {
  /**
   * The numeric prefix string to seed into the input on mount (e.g., "-", locale decimal, or "-.").
   * If provided, this takes precedence over the default value or value in seeding the input.
   *
   * This prop is only respected if the string is a valid numeric prefix to a number according to
   * `i18nIsNumericPrefixString`. We don't abstract this to seed the input with all valid cell values
   * (ie. stringified valid numbers) because the validation of `defaultStringPrefixValue` for strings that
   * include numeric digits is complex due to locale and formatting options and would duplicate the
   * logic that already exists to parse and validate keyboard input. As a result, we use the
   * `suppressDecimalFormattingOnInitialInteraction` to handle formatting when the first keystroke is
   * a number and rely on this for valid string-only prefixes to numbers.
   *
   * @default undefined
   */
  defaultStringPrefixValue?: string;

  /**
   * The initial value to show in the input in uncontrolled mode.
   *
   * ___Note:___ __This component _must_ be used in uncontrolled mode in Regrello!__ Due to the
   * underlying library we use to manage numeric-input state, a value inputted via keyboard will be
   * emitted only on blur or on increment; this means that autosaving the value and then typing some
   * more will typically result in the newly inputted value being "clobbered" when the controlled
   * autosaved value is passed back down from the parent. To avoid this, we pass in the initial
   * value as `defaultValue` and then use the `onChange` prop to listen for changes.
   */
  defaultValue?: number | null;

  /** Whether the input is non-interactive. */
  disabled?: boolean;

  /**
   * Whether to not show the '+' and '-' increment buttons. In general, we SHOULD show these for
   * standalone numeric inputs as a visual cue that a numeric value is expected, but in some
   * contexts they may be undesired (e.g., in an editable table cell).
   */
  hideIncrementButtons?: boolean;

  /** A ref to the input element. */
  inputRef?: React.Ref<HTMLInputElement>;

  /** A semantic intent to apply to this */
  intent?: Extract<RegrelloIntent, "neutral" | "warning" | "danger">;

  /**
   * A custom locale to use for this numeric input, if needed. Only provide this if you do not want
   * to use the default `i18n.locale` from Lingui.
   */
  locale?: string;

  /** The callback to call when the input value changes. */
  onChange?: (value: number | null) => void;

  /** Whether the input value can be selected but not changed by the user. */
  readOnly?: boolean;

  /** Whether value is required on this input in order for the parent form to be submitted.  */
  required?: boolean;

  /**
   * The size of this input.
   *
   * @default "large"
   */
  size?: RegrelloSize;

  /**
   * Whether this input should suppress decimal formatting on initial interaction (ie. on mount). On
   * blur or increment/decrement, normal formatting resumes.
   *
   * When true, this prevents early formatting from injecting unwanted trailing zeros when the
   * default value is the prefix of user input. (e.g., typing "123" could become "1.0023" with
   * minFractionDigits=2). This can be useful for when user types into a number cell in a table
   * (AG Grid) to simultaniously mount the editor and provide an input value.
   *
   * Mechanism: temporarily drops `minimumFractionDigits`.
   *
   * @default false
   * @example
   * ```tsx
   * <RegrelloNumericInput
   *   suppressDecimalFormattingOnInitialInteraction={true}
   *   formatOptions={{ minimumFractionDigits: 2 }}
   * />
   * // User types "123" to trigger the initial interaction -> "123"
   * // (i.e. the first keystroke is passed in as a `defaultValue` and the component is mounted)
   * // Press incremement -> "124.00"
   * // Blur -> "124.00"
   * // Mount -> "124"
   *
   * <RegrelloNumericInput
   *   suppressDecimalFormattingOnInitialInteraction={false}
   *   formatOptions={{ minimumFractionDigits: 2 }}
   * />
   * // User types "123" to trigger the initial interaction. -> "1.0023"
   * // (i.e. the first keystroke is passed in as a `defaultValue` and the component is mounted)
   * // Press increment. -> "2.00"
   * // Blur -> "2.00"
   * // Mount -> "2.00"
   * ```
   */
  suppressDecimalFormattingOnInitialInteraction?: boolean;
}

/**
 * A form input that allows the user to enter a number, and increment or decrement the value using
 * stepper buttons.
 */
export const RegrelloNumericInput = React.memo<RegrelloNumericInputProps>(function RegrelloNumericInputFn({
  className,
  defaultStringPrefixValue,
  defaultValue,
  disabled: isDisabled = false,
  formatOptions,
  hideIncrementButtons = false,
  id,
  inputRef: propsInputRef,
  intent,
  locale = i18n.locale,
  maxValue,
  minValue,
  name,
  onBlur,
  onChange: propsOnChange,
  readOnly: isReadOnly = false,
  size = INPUT_V2_DEFAULT_SIZE,
  startElement,
  step,
  suppressDecimalFormattingOnInitialInteraction = false,
  onKeyDownCapture,
  ...restProps
}) {
  const handleChange = useCallback(
    // Maps from a `number` in this scope to a `number | null` in the parent scope.
    (value: number) => {
      if (Number.isNaN(value)) {
        propsOnChange?.(null);
      } else {
        propsOnChange?.(value);
      }
    },
    [propsOnChange],
  );

  const handleInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      if (raw.length === 0) {
        propsOnChange?.(null);
        return;
      }
      if (i18nIsNumericPrefixString(raw, locale, formatOptions?.maximumFractionDigits)) {
        // noop. If it is a valid prefix to a number (e.g. "-."), we don't want to force an update.
        return;
      }
      const parsed = i18nParseNumber(raw, locale);
      if (Number.isNaN(parsed)) {
        propsOnChange?.(null);
        return;
      }
      propsOnChange?.(parsed);
    },
    [propsOnChange, locale, formatOptions?.maximumFractionDigits],
  );

  const memoizedFormatOptions = useMemoizedFormatOptions(formatOptions);

  // See the `suppressDecimalFormattingOnInitialInteraction` prop comment for more details.
  const suppressedDecimalFormatOptions = React.useMemo(() => {
    const { minimumFractionDigits, ...rest } = memoizedFormatOptions;
    return rest as Intl.NumberFormatOptions;
  }, [memoizedFormatOptions]);

  const [isDecimalFormattingSuppressed, setIsDecimalFormattingSuppressed] = useState(
    suppressDecimalFormattingOnInitialInteraction,
  );

  const appliedFormatOptions = React.useMemo(
    () => (isDecimalFormattingSuppressed ? suppressedDecimalFormatOptions : memoizedFormatOptions),
    [isDecimalFormattingSuppressed, suppressedDecimalFormatOptions, memoizedFormatOptions],
  );

  // HACKHACK (clewis): More silliness here to avoid controlled/uncontrolled value clobbering: we
  // cannot spread ...restProps here, because that risks triggering a re-render of the input state
  // if e.g. we pass in an unmemoized callback or something, which would clobber the uncontrolled
  // value with the last-saved controlled value.
  //
  // Thus, we specify only a few props here that we know we want to listen for, and we take care to
  // memoize *all* of them!
  const state = useNumberFieldState({
    // Only affects initial render, and is a primitive type:
    defaultValue: defaultValue === null ? undefined : defaultValue,

    // Memoized via being a primitive type:
    isDisabled,

    // Memoized via being a primitive type:
    isReadOnly,

    // Memoized via useMemoizedFormatOptions and conditionally adjusted via appliedFormatOptions:
    formatOptions: appliedFormatOptions,

    // Memoized via being a primitive type:
    label: null,

    // Memoized via being a primitive type:
    locale,

    // Memoized via being a primitive type:
    maxValue,

    // Memoized via being a primitive type:
    minValue,

    // Memoized via useCallback:
    onChange: handleChange,

    // Memoized via being a primitive type:
    step,
  });

  const inputRef = useRef<HTMLInputElement | null>(null);
  const incrementButtonRef = useRef<HTMLButtonElement | null>(null);
  const decrementButtonRef = useRef<HTMLButtonElement | null>(null);
  const isIncrementButtonMouseDown = useRef(false);
  const isDecrementButtonMouseDown = useRef(false);

  // React Aria's useNumberField hook requires us to specify an explicit `aria-label` or
  // `aria-labelledby` prop, even if we specify an `htmlFor` prop on the label rendered elsewhere.
  const labelId = id != null ? getAccessibleInputLabelId({ inputId: id }) : undefined;

  const { groupProps, inputProps, incrementButtonProps, decrementButtonProps } = useNumberField(
    {
      ...restProps,
      "aria-labelledby": labelId ?? restProps["aria-labelledby"],
      isDisabled,
      isReadOnly,
      isWheelDisabled: true, // BUGUX-197: Disable changing the value on scroll. No one expects it and everyone gets flustered by it.
      onChange: handleChange,
      onInput: handleInput,
      label: null,
    },
    state,
    inputRef,
  );

  const { size: _incompatibleSize, type: _incompatibleType, ...spreadableInputProps } = inputProps;
  const {
    allowFocusWhenDisabled: _incompatibleIncrementAllowFocusWhenDisabled,
    "aria-labelledby": _incompatibleIncrementAriaLabelledby,
    isDisabled: _incompatibleIncrementIsDisabled,
    excludeFromTabOrder: _incompatibleIncrementExcludeFromTabOrder,
    onPressStart: _incompatibleIncrementOnPressStart,
    onPressEnd: _incompatibleIncrementOnPressEnd,
    preventFocusOnPress: _incompatibleIncrementPreventFocusOnPress,
    ...spreadableIncrementButtonProps
    // HACKHACK (clewis): Fix unexpected and type-invalid runtime error of the form
    // "installHook.js:1 React does not recognize the `allowFocusWhenDisabled` prop on a DOM
    // element.".
  } = incrementButtonProps as typeof incrementButtonProps & { allowFocusWhenDisabled: boolean };
  const {
    allowFocusWhenDisabled: _incompatibleDecrementAllowFocusWhenDisabled,
    "aria-labelledby": _incompatibleDecrementAriaLabelledby,
    isDisabled: _incompatibleDecrementIsDisabled,
    excludeFromTabOrder: _incompatibleDecrementExcludeFromTabOrder,
    onPressStart: _incompatibleDecrementOnPressStart,
    onPressEnd: _incompatibleDecrementOnPressEnd,
    preventFocusOnPress: _incompatibleDecrementPreventFocusOnPress,
    ...spreadableDecrementButtonProps
    // HACKHACK (clewis): Fix unexpected and type-invalid runtime error of the form
    // "installHook.js:1 React does not recognize the `allowFocusWhenDisabled` prop on a DOM
    // element.".
  } = decrementButtonProps as typeof decrementButtonProps & { allowFocusWhenDisabled: boolean };

  const onIncrementButtonMouseDown = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setIsDecimalFormattingSuppressed(false);
      incrementButtonProps.onPressStart?.(convertMouseOrKeyEventToPressStartEvent(event));
      isIncrementButtonMouseDown.current = true;
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [incrementButtonProps],
  );

  const onIncrementButtonMouseUp = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      incrementButtonProps.onPressEnd?.(convertMouseOrKeyEventToPressEndEvent(event));
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [incrementButtonProps],
  );

  const onIncrementButtonMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const isFocused =
        document.activeElement === incrementButtonRef.current || document.activeElement === inputRef.current;
      if (!isFocused || !isIncrementButtonMouseDown.current) {
        return;
      }
      // Stop incrementing when the mouse leaves the button.
      onIncrementButtonMouseUp(event);
    },
    [onIncrementButtonMouseUp],
  );

  const onIncrementButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      incrementButtonProps.onPress?.(convertMouseEventToPressEvent(event));
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [incrementButtonProps],
  );

  const onDecrementButtonMouseDown = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setIsDecimalFormattingSuppressed(false);
      decrementButtonProps.onPressStart?.(convertMouseOrKeyEventToPressStartEvent(event));
      isDecrementButtonMouseDown.current = true;
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [decrementButtonProps],
  );

  const onDecrementButtonMouseUp = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      decrementButtonProps.onPressEnd?.(convertMouseOrKeyEventToPressEndEvent(event));
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [decrementButtonProps],
  );

  const onDecrementButtonMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const isFocused =
        document.activeElement === decrementButtonRef.current || document.activeElement === inputRef.current;
      if (!isFocused || !isDecrementButtonMouseDown.current) {
        return;
      }
      // Stop decrementing when the mouse leaves the button.
      onDecrementButtonMouseUp(event);
    },
    [onDecrementButtonMouseUp],
  );

  const onDecrementButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      decrementButtonProps.onPress?.(convertMouseEventToPressEvent(event));
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [decrementButtonProps],
  );

  const inputPropsOnBlur = inputProps.onBlur;

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      // Collapse non-number numeric prefix to null on blur.
      const value = event.currentTarget.value;
      if (value.length === 0 || i18nIsNumericPrefixString(value, locale, formatOptions?.maximumFractionDigits)) {
        propsOnChange?.(null);
      }
      inputPropsOnBlur?.(event);
      onBlur?.(event);
    },
    [inputPropsOnBlur, propsOnChange, onBlur, locale, formatOptions?.maximumFractionDigits],
  );

  const handlePaste: React.ClipboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const value = event.clipboardData.getData("text");
      if (value.length === 0) {
        propsOnChange?.(null);
        return;
      }

      if (formatOptions?.maximumFractionDigits === 0) {
        // (clewis): Needs special handling, as:
        // - Paste doesn't seem to work when pasting a decimal number into an integer-only field.
        // - We only support pasting US-style numbers (e.g., 1,234,567.89)
        //
        // We could consider introducing some heuristics to determine if the number is invalid vs.
        // in a different locale format (e.g., multiple period- or space-delimeted chunks,
        // optionally followed by a comma then more digits).
        const valueWithoutThousandsSeparators = value.replaceAll(",", "");
        const valueWithCorrectDecimalPlaces = formatNumber(Number(valueWithoutThousandsSeparators), locale, {
          decimalPlaces: formatOptions.maximumFractionDigits,
        });
        const valueWithCorrectDecimalPlacesButNoThousandsSeparators = valueWithCorrectDecimalPlaces.replaceAll(",", "");
        const valueWithCorrectDecimalPlacesButNoThousandsSeparatorsAsNumber = Number(
          valueWithCorrectDecimalPlacesButNoThousandsSeparators,
        );
        if (Number.isFinite(valueWithCorrectDecimalPlacesButNoThousandsSeparatorsAsNumber)) {
          propsOnChange?.(valueWithCorrectDecimalPlacesButNoThousandsSeparatorsAsNumber);
        }
        return;
      }

      // Must do this check after the special handling above, else pasting values from other locales
      // doesn't happen best-effort (we'd just resolve to null).
      const valueAsNumber = Number(value);
      if (Number.isNaN(valueAsNumber)) {
        propsOnChange?.(null);
        return;
      }

      propsOnChange?.(valueAsNumber);
    },
    [formatOptions?.maximumFractionDigits, locale, propsOnChange],
  );

  const buttonClassName = clsx({
    "w-4 h-4": size === "x-small",
    "w-5 h-5": size === "small",
  });

  // Prefer explicit defaultStringPrefixValue to seed the input, only when provided and valid.
  useMount(() => {
    if (
      defaultStringPrefixValue == null ||
      !i18nIsNumericPrefixString(defaultStringPrefixValue, locale, formatOptions?.maximumFractionDigits)
    ) {
      return;
    }
    state.setInputValue(defaultStringPrefixValue);
  });

  return (
    <div className="flex" {...groupProps}>
      <RegrelloInput
        {...spreadableInputProps}
        className={clsx(spreadableInputProps.className, className)}
        endElement={
          hideIncrementButtons
            ? undefined
            : {
                type: "interactive",
                element: (
                  <RegrelloButtonGroup variant="ghost">
                    <RegrelloButton
                      {...spreadableDecrementButtonProps}
                      ref={decrementButtonRef}
                      aria-label="Decrement"
                      className={buttonClassName}
                      disabled={decrementButtonProps.isDisabled}
                      iconOnly={true}
                      onClick={onDecrementButtonClick}
                      onMouseDown={onDecrementButtonMouseDown}
                      onMouseLeave={onDecrementButtonMouseLeave}
                      onMouseUp={onDecrementButtonMouseUp}
                      size={getButtonSizeForInputSize(size)}
                      startIcon="subtract"
                      tabIndex={-1} // (clewis): Make non-focusable; only the input should be focusable.
                      variant="ghost"
                    />
                    <RegrelloButton
                      {...spreadableIncrementButtonProps}
                      ref={incrementButtonRef}
                      aria-label="Increment"
                      className={buttonClassName}
                      disabled={incrementButtonProps.isDisabled}
                      iconOnly={true}
                      onClick={onIncrementButtonClick}
                      onMouseDown={onIncrementButtonMouseDown}
                      onMouseLeave={onIncrementButtonMouseLeave}
                      onMouseUp={onIncrementButtonMouseUp}
                      size={getButtonSizeForInputSize(size)}
                      startIcon="add"
                      tabIndex={-1} // (clewis): Make non-focusable; only the input should be focusable.
                      variant="ghost"
                    />
                  </RegrelloButtonGroup>
                ),
              }
        }
        fullWidth={true}
        id={id}
        inputRef={mergeRefs(inputRef, propsInputRef)}
        intent={intent}
        name={name}
        onBlur={handleBlur}
        onKeyDownCapture={onKeyDownCapture}
        onPaste={handlePaste}
        readOnly={isReadOnly}
        size={size}
        startElement={startElement}
      />
    </div>
  );
});

function convertMouseOrKeyEventToPressStartEvent(
  event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
) {
  return {
    type: "pressstart" as const,
    pointerType: "mouse" as const,
    target: event.currentTarget,
    shiftKey: event.shiftKey,
    ctrlKey: event.ctrlKey,
    metaKey: event.metaKey,
    altKey: event.altKey,
    continuePropagation: noop, // (clewis): Ignore.
    x: 0,
    y: 0,
  };
}

function convertMouseOrKeyEventToPressEndEvent(
  event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
) {
  return {
    type: "pressend" as const,
    pointerType: "mouse" as const,
    target: event.currentTarget,
    shiftKey: event.shiftKey,
    ctrlKey: event.ctrlKey,
    metaKey: event.metaKey,
    altKey: event.altKey,
    continuePropagation: noop, // (clewis): Ignore.
    x: 0,
    y: 0,
  };
}

function convertMouseEventToPressEvent(
  event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
) {
  return {
    type: "press" as const,
    pointerType: "mouse" as const,
    target: event.currentTarget,
    shiftKey: event.shiftKey,
    ctrlKey: event.ctrlKey,
    metaKey: event.metaKey,
    altKey: event.altKey,
    continuePropagation: noop, // (clewis): Ignore.
    x: 0,
    y: 0,
  };
}

function getButtonSizeForInputSize(size: RegrelloSize) {
  switch (size) {
    case RegrelloSize.X_SMALL:
      return RegrelloSize.X_SMALL;
    case RegrelloSize.SMALL:
      return RegrelloSize.X_SMALL;
    case RegrelloSize.MEDIUM:
      return RegrelloSize.X_SMALL;
    case RegrelloSize.LARGE:
      return RegrelloSize.SMALL;
    case RegrelloSize.X_LARGE:
      return RegrelloSize.MEDIUM;
    default:
      assertNever(size);
  }
}

const MEMOIZED_EMPTY_OBJECT: Intl.NumberFormatOptions = {};

/**
 * Hook that memoizes `formatOptions`, which are commonly passed in as an unmemoized object and
 * which can trigger unnecessary re-renders of useNumberFieldState otherwise. (Such re-renders are a
 * common cause of "clobbering" an uncontrolled value in the numeric input.)
 */
// (clewis): Fall back to MEMOIZED_EMPTY_OBJECT to avoid the runtime warning: `useDeepCompareEffect`
// should not be used with dependencies that are all primitive values. Use React.useEffect instead.
function useMemoizedFormatOptions(formatOptions: Intl.NumberFormatOptions | undefined = MEMOIZED_EMPTY_OBJECT) {
  const [memoizedFormatOptions, setMemoizedFormatOptions] = useState(formatOptions);

  useDeepCompareEffect(() => {
    setMemoizedFormatOptions(formatOptions);
  }, [formatOptions]);

  return memoizedFormatOptions;
}
